<script setup lang="ts">
import BaseLayout from "@/layouts/BaseLayout.vue";
import {Head, router, useForm} from "@inertiajs/vue3";
import {ref} from "vue";
import SetupKeyDialog from "@/Pages/settings/authentication/two_factor_authentication/components/SetupKeyDialog.vue";
import ErrorAlert from "@/components/alerts/ErrorAlert.vue";
import InfoAlert from "@/components/alerts/InfoAlert.vue";
import {route} from "ziggy-js";

interface Props {
    qrCode: string,
    setupKey: string,
    recoveryCodes: Array<string>,
}

const props = defineProps<Props>()

defineOptions({
    layout: BaseLayout
})

const step = ref(1)
const recoveryCodesDownloaded = ref(false)

const confirmationForm = useForm({
    code: '',
})

const confirmTwoFactorAuthentication = () => {
    confirmationForm.post(route('two-factor.confirm'), {
        errorBag: "confirmTwoFactorAuthentication",
        preserveScroll: true,
        onSuccess: () => {
            step.value = 2
        },
    })
}

const goBack = () => {
    window.location.href = route('settings.authentication')
}

const downloadRecoveryCodes = () => {
    recoveryCodesDownloaded.value = true
    const link = document.createElement('a')
    link.download = 'sendcrypt-recovery-codes.txt'
    link.href = route('settings.two-factor-authentication.download')
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
}

const confirmTwoFactorEnabled = () => {
    router.visit(route('settings.authentication'))
}
</script>

<template>
    <Head title="Enable two-factor authentication" />
    <h1 class="font-weight-regular text-center mt-10">
        Enable two-factor authentication (2FA)
    </h1>
    <v-stepper
        v-model="step"
        elevation="0"
        max-width="300"
        class="mx-auto"
    >
        <v-stepper-header>
            <v-stepper-item
                :value="1"
                color="primary"
                :complete="step > 1"
            />

            <v-divider />

            <v-stepper-item
                :value="2"
                color="primary"
                :complete="step > 2"
            />
        </v-stepper-header>
    </v-stepper>
    <v-card
        border="sm"
        rounded="lg"
        elevation="0"
        max-width="750"
        class="mx-auto pa-3"
    >
        <v-form
            v-if="step === 1"
            @submit.prevent="confirmTwoFactorAuthentication"
        >
            <v-card-text>
                <div class="text-h6">
                    Setup authenticator app
                </div>
                <p class="text-grey-darken-2">
                    Authenticator apps and browser extensions like 1Password, Authy, Microsoft Authenticator, etc.
                    generate one-time passwords that are used as a second factor to verify your identity when prompted
                    during sign-in.
                </p>
                <div class="text-subtitle-2 font-weight-medium mt-5">
                    Scan the QR code
                </div>
                <div
                    v-if="qrCode"
                    class="mt-4"
                >
                    <div
                        class="border-sm rounded pa-5"
                        style="width: fit-content"
                        v-html="qrCode"
                    />
                </div>
                <p class="text-grey-darken-2 mt-5">
                    Unable to scan? You can use the
                    <setup-key-dialog
                        :setup-key="props.setupKey"
                    />
                    to manually configure your authenticator app.
                </p>
                <v-row class="mt-5">
                    <v-col
                        cols="6"
                        lg="4"
                    >
                        <label class="font-weight-bold">Verify the code from the app</label>
                        <v-text-field
                            v-model="confirmationForm.code"
                            color="primary"
                            rounded="lg"
                            variant="outlined"
                            density="compact"
                            class="mt-2"
                            placeholder="XXXXXX"
                        />
                    </v-col>
                </v-row>
                <div
                    v-if="confirmationForm.hasErrors"
                    class="mb-3"
                >
                    <error-alert class="rounded-lg">
                        {{ confirmationForm.errors.code }}
                    </error-alert>
                </div>
                <v-divider />
            </v-card-text>
            <v-card-actions>
                <v-spacer />
                <text-btn
                    rounded="lg"
                    @click="goBack"
                >
                    Cancel
                </text-btn>
                <success-btn
                    rounded="lg"
                    :disabled="confirmationForm.code.length < 6"
                    type="submit"
                    :loading="confirmationForm.processing"
                >
                    Continue
                </success-btn>
            </v-card-actions>
        </v-form>
        <div
            v-if="step === 2"
        >
            <v-card-text>
                <div class="text-h6">
                    Download your recovery codes
                </div>
                <p class="text-grey-darken-2">
                    You can use recovery codes as a second factor to authenticate in case you lose access to your device. We recommend saving them with a secure password manager such as 1Password, Authy, or Keeper.
                </p>
                <info-alert class="my-5">
                    <div class="text-subtitle-2 font-weight-bold">
                        Keep your recovery codes in a safe spot
                    </div>
                    <p class="text-grey-darken-2">
                        If you lose your device and don't have the recovery codes, you will lose access to your account.
                    </p>
                </info-alert>
                <error-alert class="my-5">
                    <div class="text-subtitle-2 font-weight-bold">
                        Set up a personal access token
                    </div>
                    <p class="text-grey-darken-2">
                        If you want to sign in from the SendCrypt CLI, you will also need a personal access token. An access token is the only password accepted by the CLI when you have 2FA enabled.
                    </p>
                </error-alert>
                <div
                    v-if="recoveryCodes"
                    class="mt-4"
                >
                    <div
                        class="border-sm rounded pa-5 bg-grey-lighten-4"
                    >
                        <v-row
                            justify="center"
                            class="my-5"
                        >
                            <v-col
                                cols="6"
                            >
                                <ul>
                                    <li
                                        v-for="recoveryCode in props.recoveryCodes"
                                        :key="recoveryCode"
                                        class="text-h6 text-grey-darken-2"
                                    >
                                        <code class="text-black font-weight-bold">{{ recoveryCode }}</code>
                                    </li>
                                </ul>
                            </v-col>
                        </v-row>
                    </div>
                </div>
                <v-row class="my-4 mr-0">
                    <v-spacer />
                    <success-btn
                        rounded="lg"
                        type="submit"
                        prepend-icon="mdi-download"
                        @click="downloadRecoveryCodes"
                    >
                        Download
                    </success-btn>
                </v-row>
                <v-divider />
            </v-card-text>
            <v-card-actions>
                <v-spacer />
                <text-btn
                    rounded="lg"
                    @click="goBack"
                >
                    Cancel
                </text-btn>
                <success-btn
                    rounded="lg"
                    :disabled="!recoveryCodesDownloaded"
                    type="submit"
                    @click="confirmTwoFactorEnabled"
                >
                    I have saved my recovery codes
                </success-btn>
            </v-card-actions>
        </div>
    </v-card>
</template>

<style scoped>

</style>
